import React, { useContext, useEffect, useState } from "react";
import { Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Spinner, HStack, VStack, Input, Button, useToast, Select, Badge, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, FormControl, FormLabel } from "@chakra-ui/react";
import { Navigate } from "react-router-dom";
import AuthContext from "../../../contexts/Auth";
import client from "../../../helpers/Client";
import Pagination from "../../../components/pagination/Pagination";
import ExportButton from "../../../components/export/ExportButton";
import urlToId from "../../../helpers/UrlToId";

export default function AdminUnits() {
  const auth = useContext(AuthContext);
  const isSuperUser = auth.data?.user?.is_superuser;

  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [numOfPages, setNumOfPages] = useState(1);
  const [numOfItems, setNumOfItems] = useState(0);

  const [projectId, setProjectId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [projects, setProjects] = useState([]);

  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const [form, setForm] = useState({ project: "", apt_num: "" });
  const [editUnit, setEditUnit] = useState(null);
  const toast = useToast();

  useEffect(() => {
    if (!isSuperUser) return;
    fetchUnits(1);
    fetchProjects();
  }, [isSuperUser]);

  const fetchUnits = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", page);
      if (projectId) params.set("project", projectId);
      if (customerId) params.set("customer", customerId);
      const { data } = await client.get(`/units/?${params.toString()}`);
      const items = data.results || data;
      setUnits(items);
      setNumOfPages(data.num_of_pages || data.total_pages || 1);
      setNumOfItems(data.num_of_items || data.count || items.length || 0);
      setCurrentPage(data.current_page || page);
    } catch (e) {
      toast({ title: "Üniteler alınamadı", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const { data } = await client.get(`/projects/`);
      const items = data.results || data;
      setProjects(items);
    } catch (e) {
      // Sessiz geç, sadece formda eksik liste olur
    }
  };

  const handleDelete = async (u) => {
    try {
      await client.delete(u.url);
      toast({ title: "Ünite silindi", status: "success" });
      fetchUnits(currentPage);
    } catch (error) {
      toast({ title: "Silme başarısız", description: error.response?.data || String(error), status: "error" });
    }
  };

  const onEdit = (u) => {
    setEditUnit(u);
    setForm({ project: u.project?.url || "", apt_num: u.apt_num || "" });
    onEditOpen();
  };

  const saveEdit = async () => {
    if (!editUnit) return;
    try {
      const payload = {
        apt_num: form.apt_num ? Number(form.apt_num) : null,
      };
      if (form.project) payload.project = form.project;
      await client.put(`/units/${urlToId(editUnit.url)}/`, payload);
      toast({ title: "Ünite güncellendi", status: "success" });
      onEditClose();
      setEditUnit(null);
      fetchUnits(currentPage);
    } catch (error) {
      toast({ title: "Güncelleme başarısız", description: error.response?.data || String(error), status: "error" });
    }
  };

  const createUnit = async () => {
    try {
      const payload = {
        apt_num: form.apt_num ? Number(form.apt_num) : null,
      };
      if (form.project) payload.project = form.project;
      await client.post(`/units/`, payload);
      toast({ title: "Ünite oluşturuldu", status: "success" });
      onCreateClose();
      setForm({ project: "", apt_num: "" });
      fetchUnits(1);
    } catch (error) {
      toast({ title: "Oluşturma başarısız", description: error.response?.data || String(error), status: "error" });
    }
  };

  if (!isSuperUser) {
    return <Navigate to="/ana-sayfa/" replace />;
  }

  return (
    <Box p={6}>
      <Heading size="md" mb={4}>Ünite Yönetimi</Heading>

      <VStack align="stretch" spacing={4} mb={4}>
        <HStack spacing={3}>
          <Input placeholder="Proje ID" value={projectId} onChange={(e) => setProjectId(e.target.value)} w="160px" />
          <Input placeholder="Müşteri ID" value={customerId} onChange={(e) => setCustomerId(e.target.value)} w="160px" />
          <Button colorScheme="blue" onClick={() => { setCurrentPage(1); fetchUnits(1); }}>Filtrele</Button>
          <Button colorScheme="green" variant="solid" onClick={onCreateOpen}>Yeni Ünite</Button>
          <ExportButton
            data={(units || []).map(u => ({
              id: urlToId(u.url),
              ad: u.name || "",
              proje: u.project?.name || '-',
              acik_ticket: u.num_of_open_tickets ?? 0,
              kapali_ticket: u.num_of_closed_tickets ?? 0,
            }))}
            filename="uniteler"
            columns={[
              { key: "id", label: "ID" },
              { key: "ad", label: "Ad" },
              { key: "proje", label: "Proje" },
              { key: "acik_ticket", label: "Açık Ticket" },
              { key: "kapali_ticket", label: "Kapalı Ticket" },
            ]}
            sheetName="Üniteler"
            excelOptions={{ title: "Üniteler" }}
          />
        </HStack>
      </VStack>

      {loading ? (
        <Spinner />
      ) : (
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Ad</Th>
              <Th>Proje</Th>
              <Th>Açık Ticket</Th>
              <Th>Kapalı Ticket</Th>
              <Th>Aksiyon</Th>
            </Tr>
          </Thead>
          <Tbody>
            {units.map((u) => (
              <Tr key={u.url}>
                <Td>{u.name}</Td>
                <Td>{u.project?.name || '-'}</Td>
                <Td><Badge colorScheme="orange">{u.num_of_open_tickets ?? 0}</Badge></Td>
                <Td><Badge colorScheme="green">{u.num_of_closed_tickets ?? 0}</Badge></Td>
                <Td>
                  <HStack spacing={2}>
                    <Button size="xs" colorScheme="blue" variant="outline" onClick={() => onEdit(u)}>Düzenle</Button>
                    <Button size="xs" colorScheme="red" variant="outline" onClick={() => handleDelete(u)}>Sil</Button>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      <Box mt={4}>
        <Pagination path={"admin/uniteler"} num_of_items={numOfItems} num_of_pages={numOfPages} currentPage={currentPage} onPageChange={(p) => fetchUnits(p)} />
      </Box>

      {/* Create Modal */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Yeni Ünite</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>Proje</FormLabel>
                <Select placeholder="Seçiniz" value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })}>
                  {projects.map((p) => (
                    <option key={p.url} value={p.url}>{p.name}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Daire Numarası</FormLabel>
                <Input type="number" value={form.apt_num} onChange={(e) => setForm({ ...form, apt_num: e.target.value })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onCreateClose}>Kapat</Button>
            <Button colorScheme="green" onClick={createUnit}>Oluştur</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={() => { onEditClose(); setEditUnit(null); }} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Üniteyi Düzenle</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>Proje</FormLabel>
                <Select placeholder="Seçiniz" value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })}>
                  {projects.map((p) => (
                    <option key={p.url} value={p.url}>{p.name}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Daire Numarası</FormLabel>
                <Input type="number" value={form.apt_num} onChange={(e) => setForm({ ...form, apt_num: e.target.value })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={() => { onEditClose(); setEditUnit(null); }}>Kapat</Button>
            <Button colorScheme="blue" onClick={saveEdit}>Kaydet</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}